import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  EXPENSE_CATEGORIES,
  formatRupees,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from '@quickreport/domain';
import { Button, EmptyState, Field, Input, Select } from '@quickreport/ui';
import {
  type ExpenseFilters,
  type ExpenseRow,
  type ExpenseSummary,
  expenseCategoriesInUse,
  expenseListQuery,
  expenseSummary,
  expenseTotalQuery,
} from '@quickreport/workspace';
import { ArrowDownRight, ArrowUpRight, Paperclip, Plus, Receipt, Search, X } from 'lucide-react';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { DEFAULT_PAGE, Pagination } from '../../components/Pagination';
import { useFileUrls } from '../../lib/file-urls';
import { useWorkspace } from '../../lib/workspace';
import { ExpenseDialog } from './ExpenseDialog';

const day = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export function ExpensesPage() {
  const ws = useWorkspace();
  const [q, setQ] = useState('');
  const dq = useDeferredValue(q);
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE);
  const [dialog, setDialog] = useState<{ expense: ExpenseRow | null } | null>(null);
  const [viewing, setViewing] = useState<string[] | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const filters: ExpenseFilters = {
    ...(dq.trim() ? { q: dq } : {}),
    ...(category ? { category } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
  const list = expenseListQuery(ws.orgId, { ...filters, page, pageSize });
  const tot = expenseTotalQuery(ws.orgId, filters);
  const rows = useLiveQuery<ExpenseRow>(list.sql, list.params);
  const totals = useLiveQuery<{ n: number; total: number }>(tot.sql, tot.params);
  const total = totals?.rows[0]?.n ?? 0;
  const sum = Number(totals?.rows[0]?.total ?? 0);
  const anyFilter = Boolean(dq || category || from || to);

  // Anything that changes the expenses re-reads the month figures and the categories.
  const stamp = useLiveQuery<{ k: string }>(
    "select concat_ws('|', count(*), max(updated_at)) as k from expenses where org_id = $1",
    [ws.orgId],
  );
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [used, setUsed] = useState<string[]>([]);
  const key = stamp?.rows[0]?.k ?? '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is the signal that an expense changed
  useEffect(() => {
    let alive = true;
    void Promise.all([
      expenseSummary(ws.db, ws.orgId),
      expenseCategoriesInUse(ws.db, ws.orgId),
    ]).then(([s, c]) => {
      if (!alive) return;
      setSummary(s);
      setUsed(c);
    });
    return () => {
      alive = false;
    };
  }, [ws.db, ws.orgId, key]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new search or page size starts again from page 1
  useEffect(() => setPage(1), [dq, category, from, to, pageSize]);
  useEffect(() => {
    const last = Math.max(1, Math.ceil(total / pageSize));
    setPage((p) => Math.min(p, last));
  }, [total, pageSize]);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 5000);
    return () => clearTimeout(t);
  }, [note]);

  const categories = useMemo(() => [...new Set([...EXPENSE_CATEGORIES, ...used])], [used]);
  const rowsList = rows?.rows ?? [];
  const delta =
    summary && summary.lastMonthPaise > 0
      ? Math.round(
          ((summary.thisMonthPaise - summary.lastMonthPaise) / summary.lastMonthPaise) * 100,
        )
      : null;
  const top = summary?.byCategory[0];

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Expenses</h1>
          <p className="mt-1 text-slate">
            What the lab spends, with the bill kept beside it. Private to the lab.
          </p>
        </div>
        <Button tone="primary" onClick={() => setDialog({ expense: null })}>
          <Plus size={16} /> Add expense
        </Button>
      </div>

      {note ? (
        <p role="status" className="mt-4 rounded-md bg-green-50 px-3 py-2 text-[13px] text-ok">
          {note}
        </p>
      ) : null}

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 max-sm:[&>*:first-child]:col-span-2">
        <Tile label="Spent this month" value={formatRupees(summary?.thisMonthPaise ?? 0)}>
          {delta === null ? (
            <span className="text-[12px] text-slate">No spending last month to compare</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[12px]">
              {/* More spending than last month is shown as the warning, less as the good news. */}
              <span
                className={`inline-flex items-center font-medium ${delta > 0 ? 'text-flag' : 'text-ok'}`}
              >
                {delta > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {delta > 0 ? '+' : '−'}
                {Math.abs(delta)}%
              </span>
              <span className="text-slate">
                vs last month ({formatRupees(summary?.lastMonthPaise ?? 0)})
              </span>
            </span>
          )}
        </Tile>
        <Tile label="Biggest this month" value={top ? top.category : '—'}>
          <span className="text-[12px] text-slate">
            {top ? formatRupees(top.paise) : 'Nothing recorded yet'}
          </span>
        </Tile>
        <Tile label={anyFilter ? 'Shown below' : 'All expenses'} value={formatRupees(sum)}>
          <span className="text-[12px] text-slate">
            {total} expense{total === 1 ? '' : 's'}
          </span>
        </Tile>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="relative min-w-60 flex-1">
          <Search size={16} className="pointer-events-none absolute top-2.5 left-2.5 text-slate" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search who was paid, notes or kind"
            aria-label="Search expenses"
            className="h-9 pl-8"
          />
        </div>
        <Field label="Kind">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 w-52"
            aria-label="Kind of expense"
          >
            <option value="">All kinds</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="From">
          <Input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-40"
            aria-label="From date"
          />
        </Field>
        <Field label="To">
          <Input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 w-40"
            aria-label="To date"
          />
        </Field>
        {anyFilter ? (
          <Button
            size="sm"
            className="mb-0.5"
            onClick={() => {
              setQ('');
              setCategory('');
              setFrom('');
              setTo('');
            }}
          >
            <X size={13} /> Clear
          </Button>
        ) : null}
      </div>

      {rows && rowsList.length === 0 ? (
        anyFilter ? (
          <EmptyState title="No expense matches">Try clearing the search or the dates.</EmptyState>
        ) : (
          <EmptyState title="No expenses yet">
            Press “Add expense” to record rent, reagents or salaries. You can attach a photo of the
            bill.
          </EmptyState>
        )
      ) : (
        <>
          <ul className="mt-3 grid gap-2 md:hidden" aria-label="Expenses">
            {rowsList.map((e) => (
              <li key={e.id} className="rounded-xl border border-line bg-paper p-3">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setDialog({ expense: e })}
                    aria-label={`Open expense ${e.vendor || e.category}`}
                  >
                    <span className="block leading-snug font-medium">{e.vendor || e.category}</span>
                    <span className="mt-0.5 block text-[12px] text-slate">
                      {[
                        day(e.spent_on),
                        e.vendor ? e.category : null,
                        PAYMENT_METHOD_LABELS[e.method as PaymentMethod] ?? e.method,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {e.note ? (
                      <span className="mt-0.5 block truncate text-[12px] text-slate">{e.note}</span>
                    ) : null}
                  </button>
                  <div className="text-right">
                    <div className="tabular font-semibold">
                      {formatRupees(Number(e.amount_paise))}
                    </div>
                    {e.file_ids.length ? (
                      <button
                        type="button"
                        className="mt-1 inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12px] text-brand active:bg-brand-soft"
                        onClick={() => setViewing(e.file_ids)}
                        aria-label={`See ${e.file_ids.length} bill${e.file_ids.length === 1 ? '' : 's'}`}
                      >
                        <Paperclip size={13} /> {e.file_ids.length}
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 hidden overflow-hidden rounded-lg border border-line bg-paper md:block">
            <table className="w-full text-left">
              <thead className="bg-canvas text-[12px] tracking-wide text-slate uppercase">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Paid to</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">Paid by</th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="w-16 px-2 py-2">
                    <span className="sr-only">Bills</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsList.map((e) => (
                  <tr key={e.id} className="border-t border-line hover:bg-brand-soft/50">
                    <td className="px-4 py-2.5 whitespace-nowrap">{day(e.spent_on)}</td>
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        className="text-left font-medium text-ink hover:text-brand"
                        onClick={() => setDialog({ expense: e })}
                        aria-label={`Open expense ${e.vendor || e.category} of ${formatRupees(Number(e.amount_paise))}`}
                      >
                        {e.vendor || <span className="text-slate">—</span>}
                      </button>
                      {e.note ? (
                        <div className="max-w-xs truncate text-[12px] text-slate" title={e.note}>
                          {e.note}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-slate">{e.category}</td>
                    <td className="px-4 py-2.5 text-slate">
                      {PAYMENT_METHOD_LABELS[e.method as PaymentMethod] ?? e.method}
                    </td>
                    <td className="tabular px-4 py-2.5 text-right font-medium">
                      {formatRupees(Number(e.amount_paise))}
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      {e.file_ids.length ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] text-brand hover:bg-brand-soft"
                          onClick={() => setViewing(e.file_ids)}
                          aria-label={`See ${e.file_ids.length} bill${e.file_ids.length === 1 ? '' : 's'}`}
                        >
                          <Paperclip size={13} /> {e.file_ids.length}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        noun="expenses"
      />

      {dialog ? (
        <ExpenseDialog
          expense={dialog.expense}
          categories={used}
          onClose={() => setDialog(null)}
          onSaved={(m) => {
            setDialog(null);
            setNote(m);
          }}
        />
      ) : null}
      {viewing ? <BillViewer ids={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}

function Tile({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-line bg-paper p-4">
      <div className="flex items-center gap-1.5 text-[12px] text-slate">
        <Receipt size={13} /> {label}
      </div>
      <div className="mt-1 truncate text-[22px] leading-tight font-semibold">{value}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/** The bills of one expense, large, one after another. */
function BillViewer({ ids, onClose }: { ids: string[]; onClose: () => void }) {
  const urls = useFileUrls(ids);
  const [i, setI] = useState(0);
  const id = ids[i] as string;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setI((n) => Math.min(ids.length - 1, n + 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(0, n - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ids.length, onClose]);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Bill"
    >
      <button
        type="button"
        className="absolute top-4 right-4 rounded-full bg-white px-3 py-1.5 text-[13px] font-semibold"
        onClick={onClose}
      >
        Close
      </button>
      <div className="grid max-h-full max-w-4xl gap-3">
        {urls[id] ? (
          <img
            src={urls[id] as string}
            alt={`Bill ${i + 1} of ${ids.length}`}
            className="max-h-[80vh] rounded-lg bg-white object-contain"
          />
        ) : (
          <p className="rounded-lg bg-paper p-6 text-slate">
            This picture has not reached this computer yet. It arrives with the next sync.
          </p>
        )}
        {ids.length > 1 ? (
          <div className="flex items-center justify-center gap-3 text-white">
            <Button size="sm" disabled={i === 0} onClick={() => setI(i - 1)}>
              Previous
            </Button>
            <span className="text-[13px]">
              {i + 1} of {ids.length}
            </span>
            <Button size="sm" disabled={i === ids.length - 1} onClick={() => setI(i + 1)}>
              Next
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
